import { Sparkline } from '@/components/charts/Sparkline';
import { MetricValue } from '@/components/metrics/MetricValue';
import { TrendBadge } from '@/components/stats/TrendBadge';
import type { TrendPolarity } from '@/components/stats/trend-tone';
import { Tooltip } from '@/components/ui/tooltip';
import { getComparisonText } from '@/lib/metric-utils';
import { Box, Flex, HStack, Skeleton, Text, VStack, chakra } from '@chakra-ui/react';
import type { StatsMetric } from '@fluenceur/types';
import { InfoCircle } from 'iconoir-react';
import { useTranslation } from 'react-i18next';

export interface DashboardStatCardChart {
  data: { value: number }[];
  type: 'area' | 'bar';
  tooltipLabel?: string;
}

interface DashboardStatCardProps {
  label: string;
  metric?: StatsMetric;
  isLoading?: boolean;
  /** What a rise means on this metric; decides the delta pill colour. */
  polarity: TrendPolarity;
  /** Explainer shown behind an info icon next to the label. */
  infoTip?: string;
  /** Mini chart in the bottom-right corner; none when absent. */
  chart?: DashboardStatCardChart;
}

const MINI_CHART_STYLE = {
  area: { color: 'blue.600', fillOpacity: [0.14, 0] as [number, number] },
  bar: { color: 'blue.300', fillOpacity: [1, 1] as [number, number] },
};

const InfoTip: React.FC<{ content: string }> = ({ content }) => (
  <Tooltip content={content} showArrow portalled positioning={{ placement: 'top' }}>
    <chakra.button
      type="button"
      aria-label={content}
      display="inline-flex"
      flexShrink={0}
      color="inkSubtle"
      cursor="help"
      borderRadius="full"
      _focusVisible={{ outline: '2px solid', outlineColor: 'border.emphasized' }}
    >
      <InfoCircle width={14} height={14} aria-hidden="true" />
    </chakra.button>
  </Tooltip>
);

const StatCardSkeleton: React.FC = () => (
  <VStack alignItems="flex-start" gap={3} width="full" data-testid="dashboard-stat-skeleton">
    <Skeleton height="10px" width="55%" borderRadius="full" />
    <Skeleton height="26px" width="45%" borderRadius="full" />
    <Skeleton height="18px" width="60px" borderRadius="6px" />
  </VStack>
);

const MiniChart: React.FC<{ chart: DashboardStatCardChart }> = ({ chart }) => {
  if (chart.data.length < 2) return null;
  const style = MINI_CHART_STYLE[chart.type];
  return (
    <Box
      flex="0 1 96px"
      minW="48px"
      height="32px"
      overflow="hidden"
      data-testid="dashboard-stat-mini-chart"
    >
      <Sparkline
        inline
        data={chart.data}
        type={chart.type}
        color={style.color}
        fillOpacity={style.fillOpacity}
        tooltipLabel={chart.tooltipLabel}
      />
    </Box>
  );
};

/**
 * One dashboard KPI: uppercase label, big value, a delta pill coloured by what the
 * change means, an optional mini chart, and the period the change compares against.
 */
export const DashboardStatCard: React.FC<DashboardStatCardProps> = ({
  label,
  metric,
  isLoading = false,
  polarity,
  infoTip,
  chart,
}) => {
  const { t } = useTranslation();
  const comparisonText = metric ? getComparisonText(metric, t) : undefined;

  return (
    <VStack
      role="group"
      aria-label={label}
      alignItems="stretch"
      gap="6px"
      minHeight="132px"
      minW={0}
      pt={4}
      px={4}
      pb="14px"
      bg="cardBg"
      border="1px solid"
      borderColor="border"
      borderRadius="16px"
      transition="border-color 0.15s ease"
      _hover={{ borderColor: 'border.emphasized' }}
    >
      {isLoading && !metric ? (
        <StatCardSkeleton />
      ) : (
        <>
          <HStack gap={1.5} minW={0}>
            <Text
              fontFamily="heading"
              fontSize="11.5px"
              fontWeight="semibold"
              letterSpacing="0.06em"
              textTransform="uppercase"
              color="navInactive"
              truncate
            >
              {label}
            </Text>
            {infoTip && <InfoTip content={infoTip} />}
          </HStack>

          <Text
            fontFamily="heading"
            fontSize="28px"
            fontWeight="semibold"
            letterSpacing="-0.025em"
            lineHeight="1.15"
            color="ink"
            css={{ fontVariantNumeric: 'tabular-nums' }}
            truncate
          >
            <MetricValue metric={metric} />
          </Text>

          {/* The chart shares the pill's row, never the comparison line's: a long
              period label ("vs 31 derniers jours") would otherwise run under it. */}
          <VStack alignItems="stretch" gap={1} mt="auto" minW={0}>
            <Flex align="flex-end" justify="space-between" gap={3} minW={0}>
              {metric?.trend && (
                <TrendBadge trend={metric.trend} polarity={polarity} appearance="pill" />
              )}
              {chart && <MiniChart chart={chart} />}
            </Flex>
            {comparisonText && (
              <Text fontSize="12px" color="inkSubtle" truncate>
                {comparisonText}
              </Text>
            )}
          </VStack>
        </>
      )}
    </VStack>
  );
};
